'use client';

import * as React from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  type SelectProps,
} from '@packages/shared/src/ui/Select/Select';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';

export interface ControlledSelectProps<T extends FieldValues = FieldValues> extends SelectProps {
  control: Control<T>;
  controllerName: Path<T>;
}
const ControlledSelect = React.forwardRef(
  <T extends FieldValues>(
    {
      error,
      helperText,
      children,
      label,
      id,
      triggerProps,
      placeholder,
      control,
      controllerName,
      ...props
    }: ControlledSelectProps<T>,
    ref: React.Ref<React.ElementRef<typeof SelectTrigger>>,
  ) => {
    const fallBackId = React.useId();
    return (
      <div data-testid="select-wrapper-ui">
        {label && (
          <Label htmlFor={id || fallBackId} className="text-size-body-sm">
            {label}
          </Label>
        )}
        <Controller
          name={controllerName}
          control={control}
          render={({ field }) => {
            // throws error if we destructured field on SelectRoot because root does not accept ref
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { ref: _, ...restField } = field;
            return (
              <SelectRoot onValueChange={field.onChange} {...props} {...restField}>
                <SelectTrigger
                  id={id || fallBackId}
                  {...triggerProps}
                  ref={ref}
                  className="bg-color-background-base"
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                {children}
              </SelectRoot>
            );
          }}
        />
        {helperText && (
          <Notification severity={error ? 'error' : 'info'} variant="feedback" className="mt-0.5">
            {helperText}
          </Notification>
        )}
      </div>
    );
  },
) as <T extends FieldValues = FieldValues>(
  props: ControlledSelectProps<T> & {
    ref?: React.Ref<React.ElementRef<typeof SelectTrigger>>;
  },
) => React.ReactElement<any>;

export { ControlledSelect };
